import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  masterCronScheduleList,
  masterCronScheduleListOverdue,
  masterCronScheduleRequestRun,
  masterCronScheduleRequestRunSuccess,
} from "../../../../store/actions";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";

import handleDateTime from "../../../../Components/Common/HandleDateTime";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import TableContainer from "../../../../Components/Common/TableContainer";
import LocalDataSearch from "../../../../Components/SearchAndFilter";
import SettingsPanel from "../../components/SettingsPanel";

const normalise = (val) => val?.toString()?.trim()?.toLowerCase() || "";

// Change #100347: the ZCron dispatcher sets ProcessLockDateTime when it claims a
// row and clears it when the job ends. After 60 minutes it treats the lock as
// stale and will reclaim the row (ZCronController stale-lock window).
const STALE_LOCK_MINUTES = 60;
const isLocked = (item) => !!item?.ProcessLockDateTime;

// Change #100350: CronJob.FlagActive = 0 means the dispatcher never runs the job.
// Absent (backend or delta not live yet) counts as active.
const isInactive = (item) => String(item?.CronJob_FlagActive) === "0";

// Change #100351: what the dispatcher can pick up next - overdue, not locked, active
const isQueued = (item) => !isLocked(item) && !isInactive(item);
const lockAgeMinutes = (item) => {
  const lockedAt = new Date(String(item.ProcessLockDateTime).replace(" ", "T"));
  if (isNaN(lockedAt)) return 0;
  return Math.floor((Date.now() - lockedAt.getTime()) / 60000);
};

const CronSchedules = () => {
  const dispatch = useDispatch();

  // ---------------------------
  // Tabs
  // ---------------------------
  const Tabs = useMemo(
    () => [
      { id: "6", name: "Queue" },
      { id: "1", name: "Overdue" },
      { id: "4", name: "Locked" },
      { id: "2", name: "All" },
      { id: "3", name: "All (This Company)" },
      { id: "5", name: "Inactive" },
    ],
    []
  );

  // ---------------------------
  // UI State
  // ---------------------------
  const [activeTab, setActiveTab] = useState("6"); // Queue opens first (Change #100351)
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  // Quick dropdown filters
  const [selectedCompanyName, setSelectedCompanyName] = useState(""); // "" = All
  const [selectedCronJobName, setSelectedCronJobName] = useState(""); // "" = All

  // ---------------------------
  // Fetch on mount
  // ---------------------------
  const refreshLists = useCallback(() => {
    dispatch(masterCronScheduleListOverdue());
    dispatch(masterCronScheduleList());
  }, [dispatch]);

  useEffect(() => {
    refreshLists();
  }, [refreshLists]);

  const { overdueList, overdueLoading, allList, allLoading, requestRunResult } =
    useSelector((state) => ({
      overdueList:
        state.cronSchedulesReducer.masterCronScheduleListOverdueData
          ?.mastercronschedule_list,
      overdueLoading:
        state.cronSchedulesReducer.masterCronScheduleListOverdueDataLoading,
      allList:
        state.cronSchedulesReducer.masterCronScheduleListData
          ?.mastercronschedule_list,
      allLoading: state.cronSchedulesReducer.masterCronScheduleListDataLoading,
      requestRunResult:
        state.cronSchedulesReducer.masterCronScheduleRequestRunData,
    }));

  // ---------------------------
  // Loading state
  // ---------------------------
  useEffect(() => {
    setLoading(!!(overdueLoading || allLoading));
  }, [overdueLoading, allLoading]);

  // ---------------------------
  // Logged in company (NAME based)
  // ---------------------------
  const loggedInCompanyName = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("authUser"))?.get_company_data
        ?.Company_BusinessName;
    } catch {
      return "";
    }
  }, []);

  // ---------------------------
  // Base data by tab
  // ---------------------------
  const baseData = useMemo(() => {
    // Inactive jobs are never going to run, so they are not "overdue"
    const overdue = (overdueList || []).filter((item) => !isInactive(item));
    const all = allList || [];

    if (activeTab === "1") return overdue;
    if (activeTab === "6") return overdue.filter(isQueued);
    if (activeTab === "2") return all;
    if (activeTab === "4") return all.filter(isLocked);
    if (activeTab === "5") return all.filter(isInactive);

    // All (This Company) — NAME based match
    if (activeTab === "3") {
      if (!loggedInCompanyName) return [];
      return all.filter(
        (item) =>
          normalise(item.Company_BusinessName) ===
          normalise(loggedInCompanyName)
      );
    }

    return all;
  }, [activeTab, overdueList, allList, loggedInCompanyName]);

  const overdueCount = useMemo(
    () => (overdueList || []).filter((item) => !isInactive(item)).length,
    [overdueList]
  );

  const queueCount = useMemo(
    () => (overdueList || []).filter(isQueued).length,
    [overdueList]
  );

  const lockedCount = useMemo(
    () => (allList || []).filter(isLocked).length,
    [allList]
  );

  // ---------------------------
  // Dropdown options (from ALL data)
  // ---------------------------
  const companyOptions = useMemo(() => {
    const map = new Set();
    (allList || []).forEach((item) => {
      if (item?.Company_BusinessName) {
        map.add(item.Company_BusinessName);
      }
    });
    return Array.from(map).sort();
  }, [allList]);

  const cronJobOptions = useMemo(() => {
    const map = new Set();
    (allList || []).forEach((item) => {
      if (item?.CronJob_Name) {
        map.add(item.CronJob_Name);
      }
    });
    return Array.from(map).sort();
  }, [allList]);

  // ---------------------------
  // Apply quick dropdown filters
  // ---------------------------
  const dataForSearch = useMemo(() => {
    let list = baseData || [];

    if (selectedCompanyName) {
      list = list.filter(
        (item) =>
          normalise(item.Company_BusinessName) ===
          normalise(selectedCompanyName)
      );
    }

    if (selectedCronJobName) {
      list = list.filter(
        (item) =>
          normalise(item.CronJob_Name) === normalise(selectedCronJobName)
      );
    }

    return list;
  }, [baseData, selectedCompanyName, selectedCronJobName]);

  // ---------------------------
  // Handle rerun cron result
  // ---------------------------
  useEffect(() => {
    if (isEmpty(requestRunResult)) return;

    if (requestRunResult?.status === 200) {
      toast.success(requestRunResult?.message);
      refreshLists();
    } else {
      toast.error(requestRunResult?.message);
    }

    dispatch(masterCronScheduleRequestRunSuccess());
  }, [requestRunResult, dispatch, refreshLists]);

  // ---------------------------
  // Table columns
  // ---------------------------
  const columns = useMemo(
    () => [
      {
        Header: "Id",
        accessor: "Id",
        Cell: ({ row }) => <span>#{row.original.Id}</span>,
      },
      {
        Header: "Business",
        accessor: "Company_BusinessName",
        Cell: ({ row }) => (
          <>
            <div>{row.original.Company_BusinessName}</div>
            <small className="text-muted">
              Company ID: #{row.original.Company_Id}
            </small>
          </>
        ),
      },
      {
        Header: "Cron Job",
        accessor: "CronJob_Name",
        Cell: ({ row }) => (
          <>
            <div>
              {row.original.CronJob_Name}
              {isInactive(row.original) && (
                <span
                  className="badge bg-secondary-subtle text-secondary ms-2"
                  title="CronJob.FlagActive = 0: the dispatcher does not run this job"
                >
                  Inactive
                </span>
              )}
            </div>
            <small className="text-muted">
              Cron Job ID: #{row.original.CronJob_Id}
            </small>
          </>
        ),
      },
      {
        Header: "Last Run",
        accessor: "LastRunDateTime",
        Cell: ({ row }) => handleDateTime(row.original.LastRunDateTime),
      },
      {
        Header: "Next Run",
        accessor: "NextRunDateTime",
        Cell: ({ row }) => handleDateTime(row.original.NextRunDateTime),
      },
      {
        Header: "Interval",
        accessor: "PhpDateInterval",
      },
      {
        Header: "Lock",
        accessor: "ProcessLockDateTime",
        Cell: ({ row }) => {
          if (!isLocked(row.original)) {
            return <span className="text-muted">-</span>;
          }
          const age = lockAgeMinutes(row.original);
          const stale = age >= STALE_LOCK_MINUTES;
          return (
            <>
              <span
                className={`badge ${
                  stale
                    ? "bg-danger-subtle text-danger"
                    : "bg-warning-subtle text-warning"
                }`}
                title={
                  stale
                    ? `Locked over ${STALE_LOCK_MINUTES} minutes: the job probably died mid-run. The dispatcher will reclaim it on its next pass.`
                    : "The dispatcher is running this job now."
                }
              >
                <i className="fa-solid fa-lock me-1" />
                {stale ? "Stale lock" : "Running"}
              </span>
              <div>
                <small className="text-muted">
                  {handleDateTime(row.original.ProcessLockDateTime)} ({age}m)
                </small>
              </div>
            </>
          );
        },
      },
      {
        Header: "Action",
        Cell: ({ row }) => (
          <UncontrolledDropdown>
            <DropdownToggle className="btn btn-soft-info btn-icon">
              <i className="fa-solid fa-ellipsis" />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem
                onClick={() =>
                  dispatch(
                    masterCronScheduleRequestRun({
                      id: row.original.Id,
                    })
                  )
                }
              >
                <i className="fa-solid fa-rotate-right me-2" />
                Re-run Cron
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    [dispatch]
  );

  // ---------------------------
  // Render
  // ---------------------------

  const tableData = useMemo(() => {
    if (searchQuery?.trim()) {
      return searchResults || [];
    }
    return dataForSearch || [];
  }, [searchQuery, searchResults, dataForSearch]);
  return (
    <SettingsPanel
      title="Cron Schedules"
      description="Scheduled background jobs across all companies and their run status."
      actions={
        <Button color="light" onClick={refreshLists} disabled={loading}>
          <i
            className={`ri-refresh-line align-middle me-1${
              loading ? " mdi-spin" : ""
            }`}
          />
          Refresh
        </Button>
      }
      tabs={Tabs.map((tab) => ({
        value: tab.id,
        label: tab.name,
        // Change #100348: count pills on Overdue + Locked only, not the All tabs
        ...(tab.id === "1" ? { count: overdueCount } : {}),
        ...(tab.id === "6" ? { count: queueCount } : {}),
        ...(tab.id === "4" ? { count: lockedCount } : {}),
      }))}
      value={activeTab}
      onChange={setActiveTab}
    >
      <LocalDataSearch
        Page="SuperAdminCronSchedules"
        ReceivedDataAPI={dataForSearch}
        setSearchInActionShowLoading={setLoading}
        activeSearchTab=""
        allowedSearchFilters={["Company_BusinessName", "CronJob_Name", "Id"]}
        searchQuery={searchQuery}
        setSearchFilteredResults={setSearchResults}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        toggleShowFilter={() => setShowFilter((v) => !v)}
        allowedFiltersInit={[]}
        allowedFilters={[]}
        setAllowedFilters={() => {}}
      />

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader className="border-0">
              <Row className="g-3 align-items-center">
                <Col sm={3}>
                  <Input
                    type="search"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Col>

                <Col sm={3}>
                  <Input
                    type="select"
                    value={selectedCompanyName}
                    onChange={(e) => setSelectedCompanyName(e.target.value)}
                  >
                    <option value="">All Companies</option>
                    {companyOptions.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col sm={3}>
                  <Input
                    type="select"
                    value={selectedCronJobName}
                    onChange={(e) => setSelectedCronJobName(e.target.value)}
                  >
                    <option value="">All Cron Jobs</option>
                    {cronJobOptions.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              {loading ? (
                <LoadingSpinner />
              ) : !isEmpty(tableData) ? (
                <TableContainer
                  columns={columns}
                  data={tableData}
                  customPageSize={10}
                  isGlobalFilter={false}
                  tableClass="align-middle"
                />
              ) : (
                <div className="text-center my-4">
                  <h5>No Results Found</h5>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </SettingsPanel>
  );
};

export default CronSchedules;
